import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { DatePicker, Select } from 'antd';
import {
  Buildings,
  CalendarBlank,
  Phone,
} from '@phosphor-icons/react';
import Button from '../../components/Button';
import Typography from '../../components/Typography';
import COLORS from '../../util/constants/colors';

const DATE_FORMAT = 'YYYY-MM-DD';
const MAX_DATE_RANGE_DAYS = 100;

const calendarAnchor = (calendarDates) => {
  const [startDate, endDate] = calendarDates || [];

  if (startDate && !endDate) return startDate;
  if (!startDate && endDate) return endDate;
  return null;
};

export const mairaRangeDisabledDate = (current, calendarDates = []) => {
  if (!current) return false;
  if (current > moment().endOf('day')) return true;

  const anchor = calendarAnchor(calendarDates);
  if (!anchor) return false;

  const earliestDate = anchor.clone().subtract(MAX_DATE_RANGE_DAYS - 1, 'days');
  const latestDate = anchor.clone().add(MAX_DATE_RANGE_DAYS - 1, 'days');

  return current.isBefore(earliestDate, 'day') || current.isAfter(latestDate, 'day');
};

const MairaFilters = ({
  dateRange,
  setDateRange,
  selectedVoiceAiNumberIds,
  setSelectedVoiceAiNumberIds,
  selectedDepartmentSlugs,
  setSelectedDepartmentSlugs,
  filterOptions,
  loading,
  onApply,
  onClear,
}) => {
  const [calendarDates, setCalendarDates] = useState([]);
  const voiceAiNumbers = filterOptions?.voice_ai_numbers || [];
  const departments = filterOptions?.departments || [];

  return (
    <div className="maira-filters">
      <div className="maira-filter-item maira-filter-date">
        <Typography variant="text" size="small" bold>
          <CalendarBlank size={16} color={COLORS.BLUE_PRIMARY_LITE} weight="bold" />
          &nbsp;
          Date Range
        </Typography>
        <DatePicker.RangePicker
          allowClear={false}
          value={[
            dateRange.startDate ? moment(dateRange.startDate, DATE_FORMAT) : null,
            dateRange.endDate ? moment(dateRange.endDate, DATE_FORMAT) : null,
          ]}
          format={DATE_FORMAT}
          disabledDate={(current) => mairaRangeDisabledDate(current, calendarDates)}
          onCalendarChange={(nextCalendarDates) => {
            setCalendarDates(nextCalendarDates || []);
          }}
          onChange={(_, dateStrings) => {
            setDateRange({
              startDate: dateStrings[0] || null,
              endDate: dateStrings[1] || null,
            });
            setCalendarDates([]);
          }}
          onOpenChange={(open) => {
            if (!open) setCalendarDates([]);
          }}
        />
      </div>
      <div className="maira-filter-item">
        <Typography variant="text" size="small" bold>
          <Phone size={16} color={COLORS.BLUE_PRIMARY_LITE} weight="bold" />
          &nbsp;
          Voice AI Numbers
        </Typography>
        <Select
          mode="multiple"
          allowClear
          placeholder="All numbers"
          value={selectedVoiceAiNumberIds}
          options={voiceAiNumbers.map((number) => ({
            value: number.id,
            label: number.label || number.number,
          }))}
          onChange={setSelectedVoiceAiNumberIds}
        />
      </div>
      {departments.length > 0 && (
        <div className="maira-filter-item">
          <Typography variant="text" size="small" bold>
            <Buildings size={16} color={COLORS.BLUE_PRIMARY_LITE} weight="bold" />
            &nbsp;
            Departments
          </Typography>
          <Select
            mode="multiple"
            allowClear
            placeholder="All departments"
            value={selectedDepartmentSlugs}
            options={departments.map((department) => ({
              value: department.id,
              label: department.name,
            }))}
            onChange={setSelectedDepartmentSlugs}
          />
        </div>
      )}
      <div className="maira-filter-actions">
        <Button
          type="secondary"
          size="xsmall"
          label="Clear"
          onClick={onClear}
          disabled={loading}
        />
        <Button
          type="primary"
          size="xsmall"
          label="Update"
          onClick={onApply}
          disabled={loading}
        />
      </div>
    </div>
  );
};

MairaFilters.propTypes = {
  dateRange: PropTypes.object.isRequired,
  setDateRange: PropTypes.func.isRequired,
  selectedVoiceAiNumberIds: PropTypes.array.isRequired,
  setSelectedVoiceAiNumberIds: PropTypes.func.isRequired,
  selectedDepartmentSlugs: PropTypes.array.isRequired,
  setSelectedDepartmentSlugs: PropTypes.func.isRequired,
  filterOptions: PropTypes.object,
  loading: PropTypes.bool,
  onApply: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

MairaFilters.defaultProps = {
  filterOptions: {},
  loading: false,
};

export default MairaFilters;
